import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { ShoppingBag, Globe, User, LogOut, Shield, Home, Sparkles, Image, Heart, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

export default function Navbar() {
  const { t, lang, toggleLang } = useLanguage();
  const { user, login, logout, isAdmin } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { href: '/catalog', label: t('nav.catalog') },
    { href: '/memory-bouquet', label: 'Memory' },
    { href: '/gallery', label: t('nav.gallery') },
    { href: '/about', label: t('nav.about') },
  ];

  const mobileNavItems = [
    { href: '/', icon: Home, label: t('nav.home') },
    { href: '/catalog', icon: ShoppingBag, label: t('nav.catalog') },
    { href: '/memory-bouquet', icon: Sparkles, label: 'Memory' },
    { href: '/gallery', icon: Image, label: t('nav.gallery') },
    { href: user ? '/wishlist' : '/login', icon: Heart, label: user ? t('nav.wishlist') : t('nav.login') },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <>
      {/* Desktop Navbar - Compact Modern Glass */}
      <nav className="sticky top-0 z-50 hidden md:block" data-testid="navbar">
        <div className="mx-4 lg:mx-8 mt-3">
          <div className="glass-card-strong rounded-2xl px-5 lg:px-6">
            <div className="flex items-center justify-between h-14">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#E8A0B8] to-[#C5DB7B] flex items-center justify-center shadow-sm">
                  <span className="text-white text-xs font-bold font-['DM_Sans']">S</span>
                </div>
                <span className="font-['Cormorant_Garamond'] text-xl font-medium text-[#2C2A29] tracking-tight">
                  Seikat
                </span>
              </Link>

              {/* Center Nav Links - Pill style */}
              <div className="flex items-center bg-white/30 rounded-xl p-1 gap-0.5">
                {navLinks.map(link => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`px-4 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-250 ${
                      isActive(link.href)
                        ? 'bg-white/80 text-[#D4859B] shadow-sm'
                        : 'text-[#6B655F] hover:text-[#2C2A29] hover:bg-white/40'
                    }`}
                    data-testid={`nav-link-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={toggleLang}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/25 text-[11px] font-semibold text-[#6B655F] hover:text-[#2C2A29] hover:bg-white/40 transition-all"
                  data-testid="language-toggle"
                >
                  <Globe size={13} strokeWidth={1.5} />
                  <span className="uppercase tracking-wider">{lang === 'en' ? 'ID' : 'EN'}</span>
                </button>

                <Link to="/wishlist" className="p-2 rounded-lg hover:bg-white/40 transition-all text-[#6B655F] hover:text-[#E8A0B8]" data-testid="nav-wishlist">
                  <Heart size={17} strokeWidth={1.5} />
                </Link>

                <Link to="/cart" className="relative p-2 rounded-lg hover:bg-white/40 transition-all text-[#6B655F] hover:text-[#2C2A29]" data-testid="nav-cart">
                  <ShoppingBag size={17} strokeWidth={1.5} />
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-gradient-to-br from-[#E8A0B8] to-[#D4859B] text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-md min-w-[18px] min-h-[18px]">
                      {itemCount}
                    </span>
                  )}
                </Link>

                {user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 p-1 rounded-xl hover:bg-white/40 transition-all" data-testid="user-menu-trigger">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-lg object-cover ring-2 ring-white/70 shadow-sm" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E8A0B8] to-[#C5DB7B] flex items-center justify-center">
                            <span className="text-white text-xs font-semibold">{user.name?.[0]}</span>
                          </div>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 glass-card-strong rounded-xl border-white/50 p-1.5 mt-1">
                      <div className="px-3 py-2 mb-1">
                        <p className="text-sm font-semibold text-[#2C2A29] truncate">{user.name}</p>
                        <p className="text-[11px] text-[#6B655F] truncate">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator className="bg-white/40" />
                      <DropdownMenuItem onClick={() => navigate('/wishlist')} className="rounded-lg text-[13px]" data-testid="menu-wishlist">
                        <Heart size={13} className="mr-2" /> {t('nav.wishlist')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/orders')} className="rounded-lg text-[13px]" data-testid="menu-orders">
                        {t('nav.my_orders')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/memories')} className="rounded-lg text-[13px]" data-testid="menu-memories">
                        <FileText size={13} className="mr-2" /> {t('nav.my_memories')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/profile')} className="rounded-lg text-[13px]" data-testid="menu-profile">
                        <User size={13} className="mr-2" /> {t('nav.profile')}
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem onClick={() => navigate('/admin')} className="rounded-lg text-[13px]" data-testid="menu-admin">
                          <Shield size={13} className="mr-2" /> {t('nav.admin')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-white/40" />
                      <DropdownMenuItem onClick={logout} className="rounded-lg text-[13px] text-red-400 focus:text-red-500" data-testid="menu-logout">
                        <LogOut size={13} className="mr-2" /> {t('nav.logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    onClick={login}
                    className="bg-[#D4859B] hover:bg-[#C07088] text-white rounded-xl px-4 py-1.5 text-[12px] font-semibold shadow-md shadow-[#D4859B]/20 btn-glow h-8"
                    data-testid="nav-login"
                  >
                    {t('nav.login')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar - Compact */}
      <nav className="sticky top-0 z-50 glass-nav md:hidden" data-testid="navbar-mobile">
        <div className="flex items-center justify-between px-4 h-12">
          <Link to="/" className="flex items-center gap-1.5" data-testid="nav-logo-mobile">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#E8A0B8] to-[#C5DB7B] flex items-center justify-center">
              <span className="text-white text-[10px] font-bold font-['DM_Sans']">S</span>
            </div>
            <span className="font-['Cormorant_Garamond'] text-lg font-medium text-[#2C2A29]">Seikat</span>
          </Link>
          <div className="flex items-center gap-1">
            <button onClick={toggleLang} className="p-1.5 rounded-lg text-[#6B655F] hover:bg-white/40" data-testid="language-toggle-mobile">
              <Globe size={15} strokeWidth={1.5} />
            </button>
            <Link to="/cart" className="relative p-1.5 rounded-lg text-[#6B655F]" data-testid="nav-cart-mobile">
              <ShoppingBag size={17} strokeWidth={1.5} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-gradient-to-br from-[#E8A0B8] to-[#D4859B] text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            {user ? (
              <button onClick={() => navigate(isAdmin ? '/admin' : '/profile')} className="p-0.5" data-testid="user-avatar-mobile">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-lg object-cover ring-1.5 ring-white/70" />
                ) : (
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#E8A0B8] to-[#C5DB7B] flex items-center justify-center">
                    <span className="text-white text-[10px] font-semibold">{user.name?.[0]}</span>
                  </div>
                )}
              </button>
            ) : (
              <button onClick={login} className="p-1.5 rounded-lg text-[#6B655F]" data-testid="nav-login-mobile">
                <User size={17} strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <div className="bottom-nav md:hidden mobile-safe-bottom" data-testid="bottom-nav">
        <div className="flex items-center justify-around px-2 pt-1.5 pb-0.5">
          {mobileNavItems.map(item => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center gap-0 px-3 py-1 rounded-xl transition-all duration-250 ${
                  active ? 'text-[#D4859B]' : 'text-[#6B655F]/50'
                }`}
                data-testid={`bottom-nav-${item.href.slice(1) || 'home'}`}
              >
                <div className={`p-1 rounded-lg transition-all ${active ? 'bg-[#E8A0B8]/12' : ''}`}>
                  <item.icon size={19} strokeWidth={active ? 2 : 1.5} />
                </div>
                <span className="text-[9px] font-semibold tracking-wide">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
